import { useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * In-app replacement for `window.prompt`, which Electron does not implement
 * (it returns null without showing anything). Resolves to the entered text, or
 * null when cancelled. Mounts its own root so callers need no host component.
 */
export function promptText(message: string, initial = ''): Promise<string | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const finish = (value: string | null) => {
      root.unmount();
      host.remove();
      resolve(value);
    };
    root.render(<PromptDialog message={message} initial={initial} onDone={finish} />);
  });
}

function PromptDialog({
  message,
  initial,
  onDone,
}: {
  message: string;
  initial: string;
  onDone: (value: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <div className="palette-backdrop" onMouseDown={() => onDone(null)}>
      <form
        className="palette"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          onDone(value);
        }}
      >
        <div className="palette-foot">{message}</div>
        <input
          className="palette-input"
          autoFocus
          value={value}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onDone(null);
          }}
        />
      </form>
    </div>
  );
}
